import Swal from "sweetalert2";
import { notify } from "@kyvg/vue3-notification";
import { createApp, h, nextTick } from "vue";
// import Modal from "@/components/Modal.vue";
import Modal from "@/components/BootstrapModal.vue";
import Auth from "@/API/Auth";

// Format date to readable format
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return "N/A";
  try {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    };

    return new Intl.DateTimeFormat("en-US", options).format(new Date(date));
  } catch (error) {
    console.error('Date formatting error:', error);
    return "Invalid Date";
  }
};

// Format date without time (short format)
export const formatDateShort = (date: string | Date | null | undefined): string => {
  if (!date) return "N/A";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(date));
  } catch (error) {
    return "Invalid Date";
  }
};

// Truncate text to specified length with ellipsis
export const truncateText = (text: string | null | undefined, maxLength: number = 50): string => {
  if (!text) return "";

  try {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  } catch (error) {
    console.error('Text truncation error:', error);
    return "";
  }
};

// Convert HTML tags to plain text (similar to jQuery's .text() method)
export const htmlToText = (html: string | null | undefined, maxLength: number = 50): string => {
  if (!html) return "";

  try {
    // Create a temporary DOM element to parse HTML
    return truncateText(htmlToTextRegex(html), maxLength);
  } catch (error) {
    console.error('HTML to text conversion error:', error);
    return "";
  }
};

export const htmlToTextRegex = (
  html: string | null | undefined
): string => {
  if (!html) return "";

  try {
    return html
      // Remove script blocks
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")

      // Remove style blocks
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")

      // Remove iframe blocks
      .replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/gi, "")

      // Remove object/embed
      .replace(/<object[^>]*>[\s\S]*?<\/object>/gi, "")
      .replace(/<embed[^>]*>/gi, "")

      // Convert line breaks
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")

      // Remove all remaining tags
      .replace(/<[^>]*>/g, "")

      // Decode common entities
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")

      .replace(/\s+/g, " ")
      .trim();
  } catch (error) {
    console.error("HTML to text regex conversion error:", error);
    return "";
  }
};

// Format date and time in short format
export const formatDateTimeShort = (date: string | Date | null | undefined): string => {
  if (!date) return "N/A";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(date));
  } catch (error) {
    return "Invalid Date";
  }
};

// Format date and time to readable format
export const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return "N/A";
  try {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };

    return new Intl.DateTimeFormat("en-US", options).format(new Date(date));
  } catch (error) {
    console.error('DateTime formatting error:', error);
    return "Invalid Date";
  }
};

export const customConfirmationMessage = async (title: string, text: string = "", icon: "warning" | "error" | "info" | "success") => {
  return await Swal.fire({
    title: title,
    text: text,
    icon: icon,
    showCancelButton: true,
    cancelButtonText: "Cancel",
    customClass: {
      confirmButton: 'pk-btn-main',
      cancelButton: 'pk-btn-secondary ms-2'
    },
    buttonsStyling: false,
    confirmButtonText: "Yes, proceed",
  });
};

export const customNotify = (title: string, text: string, type: string) => {
  notify({
    title: title,
    text: text,
    type: type,
  });
};


/**
 * Truncate text and add "See more" that opens custom Modal.vue
 */
export function truncateTextWithModal(text: string, limit = 50, fieldName: string | null = null) {
  if (!text) return "";

  // Truncate text with ellipsis
  const isTruncated = text.length > limit;
  const truncated = isTruncated ? text.substring(0, limit) + "..." : text;

  // If text not truncated — return as plain text
  if (!isTruncated) return truncated;

  // Create unique ID for this instance
  const id = `see-more-${Math.random().toString(36).slice(2, 9)}`;

  // HTML for clickable link
  const seeMoreLink = `
    <a href="#"
      id="${id}"
      class="see-more-link"
      style="color:#6E3894;cursor:pointer;text-decoration:none;font-weight:500;">
      See more
    </a>
  `;

  // Defer DOM event attachment to next render cycle
  nextTick(() => {
    const link = document.getElementById(id);
    if (!link) return;

    // Avoid attaching multiple listeners
    link.onclick = (e) => {
      e.preventDefault();

      // Create a container for dynamic modal instance
      const container = document.createElement("div");
      document.body.appendChild(container);

      // Create a temporary Vue app for modal
      const app = createApp({
        data: () => ({ show: true }),
        render() {
          return h(Modal, {
            modelValue: this.show,
            "onUpdate:modelValue": (val: boolean) => {
              this.show = val;
              if (!val) {
                // Cleanly unmount modal after transition ends
                setTimeout(() => {
                  app.unmount();
                  if (container.parentNode) container.parentNode.removeChild(container);
                }, 300);
              }
            },
            title: fieldName || "Full Content",
            hasSubmit: false,
            showFooter: false,
            size: "large",
          }, {
            default: () => h("div", { style: "white-space: pre-wrap;" }, text),
          });
        },
      });

      app.mount(container);
    };
  });

  // Return combined truncated text and clickable link
  return truncated + seeMoreLink;
};

/**
 * Check if the current user has permission for a given controller/module + action.
 *
 * Matches by permission "name" first, then checks if ANY controller
 * inside that permission block grants the requested action.
 * Falls back to direct controller-key match.
 *
 * @param controller - The controller or module name (e.g. "Team", "Dashboard")
 * @param action - The action name (e.g. "show", "insert", "assignUsers")
 * @returns true if the user has the permission, false otherwise
 */
export function hasPermission(controller: string, action: string): boolean {
  if (Auth.isSuperAdmin()) return true;

  const role = Auth.getRole();
  const permissions = role?.permissions;
  if (!Array.isArray(permissions)) return false;

  const controllerLower = controller.toLowerCase();

  for (const perm of permissions) {
    // 1) Match by permission name (route/module level) — case-insensitive
    if (perm.name?.toLowerCase() === controllerLower) {
      const controllers = perm.controllers;
      if (!controllers) continue;
      for (const ctrlKey of Object.keys(controllers)) {
        if (controllers[ctrlKey][action] === true) {
          return true;
        }
      }
    }

    // 2) Fallback: direct controller-key match — case-insensitive
    const controllers = perm.controllers;
    if (controllers) {
      const matchingKey = Object.keys(controllers).find(
        (key) => key.toLowerCase() === controllerLower
      );
      if (matchingKey && controllers[matchingKey][action] === true) {
        return true;
      }
    }
  }
  return false;
}